import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, LogOut, RotateCcw } from "lucide-react";
import { PageShell, usePageTitle } from "./site/Layout";
import { buttons, container } from "./site/ui";
import { Plate } from "./report/ReportParts";
import { AUTH_API_URL } from "../config";
import { authHeaders, getToken, signOut } from "../lib/auth";
import { formatDate } from "../lib/report";

function UserProfile() {
  usePageTitle("My searches");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      axios.get(`${AUTH_API_URL}/api/auth/profile`, { headers: authHeaders(token) }),
      axios.get(`${AUTH_API_URL}/api/auth/search-history`, { headers: authHeaders(token) }),
    ])
      .then(([profile, history]) => {
        if (cancelled) return;
        setUser(profile.data);
        setSearchHistory(Array.isArray(history.data) ? history.data : []);
      })
      .catch((requestError) => {
        if (cancelled) return;
        console.error("Error fetching user data:", requestError);
        // The backend answers 401 for a missing token and 403 for an expired one.
        if ([401, 403].includes(requestError.response?.status)) {
          signOut();
          navigate("/");
        } else {
          setError("We couldn't load your account. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, attempt]);

  const handleSignOut = () => {
    signOut();
    navigate("/");
  };

  const searches = [...searchHistory].sort((a, b) => new Date(b.searchDate) - new Date(a.searchDate));

  let content;
  if (loading) {
    content = (
      <div className="flex justify-center py-16" role="status">
        <span className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" aria-hidden="true" />
        <span className="sr-only">Loading your searches</span>
      </div>
    );
  } else if (error) {
    content = (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-slate-700">{error}</p>
        <button type="button" onClick={() => setAttempt((count) => count + 1)} className={`${buttons.primary} mt-5`}>
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  } else if (!searches.length) {
    content = (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
        <h2 className="font-semibold text-slate-900">No saved reports yet</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
          When you check a car, choose "Save to My searches" at the bottom of the report and it will appear here.
        </p>
        <Link to="/#search" className={`${buttons.primary} mt-6`}>
          Check a car
        </Link>
      </div>
    );
  } else {
    content = (
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {searches.map((search, index) => (
          <li
            key={search._id || `${search.registration}-${index}`}
            className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-center gap-4">
              {search.registration && <Plate registration={search.registration} size="sm" />}
              <div>
                <p className="font-semibold text-slate-900">
                  {[search.make, search.model].filter(Boolean).join(" ") || "Vehicle"}
                </p>
                <p className="text-sm text-slate-500">
                  Saved {formatDate(search.searchDate)}
                  {Array.isArray(search.faults) && search.faults.length > 0 &&
                    ` · ${search.faults.length} ${search.faults.length === 1 ? "fault" : "faults"} found`}
                </p>
              </div>
            </div>
            {search.registration && (
              <Link
                to={`/results?${new URLSearchParams({ registration: search.registration })}`}
                className={`${buttons.secondary} shrink-0`}
              >
                Run the check again
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <PageShell>
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} flex flex-col gap-5 py-8 sm:flex-row sm:items-end sm:justify-between sm:py-10`}>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">My searches</h1>
            {user && (
              <p className="mt-2 text-slate-600">
                {user.firstName} {user.lastName} · {user.email}
                {user.createdAt && ` · Member since ${formatDate(user.createdAt)}`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/#search" className={buttons.primary}>
              New check
            </Link>
            <button type="button" onClick={handleSignOut} className={buttons.secondary}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </section>
      <div className={`${container} py-10`}>{content}</div>
    </PageShell>
  );
}

export default UserProfile;
